/**
 * The languages the app speaks — م-7-01, and what م-7-02's filter is made of.
 *
 * Four, as the requirement says. Arabic is the product's own language; the
 * other three are chosen for the people المرحلة 7 exists for — the largest
 * populations learning to recite without Arabic as a first language.
 *
 * Urdu earns its place twice over: it is spoken by tens of millions of
 * reciters, and it is RIGHT-TO-LEFT. Until it existed, every direction
 * decision in this app could be read as "Arabic or not", and a bug that
 * assumed so would never have shown. A second RTL language is what proves the
 * layout follows the language rather than the alphabet.
 *
 * This list is data, not architecture: swapping Indonesian for Turkish or
 * French is a locale file and one entry here.
 */

export type LanguageCode = 'ar' | 'en' | 'ur' | 'id';

export type LanguageInfo = {
  code: LanguageCode;
  /** The language's own name, as its speakers write it — never translated. */
  endonym: string;
  rtl: boolean;
};

export const LANGUAGES: LanguageInfo[] = [
  { code: 'ar', endonym: 'العربية', rtl: true },
  { code: 'en', endonym: 'English', rtl: false },
  { code: 'ur', endonym: 'اردو', rtl: true },
  { code: 'id', endonym: 'Bahasa Indonesia', rtl: false },
];

const BY_CODE = new Map(LANGUAGES.map((l) => [l.code, l]));

/** True for one of the app's own four. Anything else is not offered. */
export function isLanguage(code: unknown): code is LanguageCode {
  return typeof code === 'string' && BY_CODE.has(code as LanguageCode);
}

/** Whether a language reads right to left. Unknown codes read left to right. */
export function isRTLLanguage(code: unknown): boolean {
  return isLanguage(code) ? BY_CODE.get(code)!.rtl : false;
}

/** The language's own name, or the code itself when it is not one of ours. */
export function endonym(code: unknown): string {
  return isLanguage(code) ? BY_CODE.get(code)!.endonym : String(code ?? '');
}

/**
 * What a معلم declared, cleaned.
 *
 * Order follows LANGUAGES rather than what was stored, so two teachers who
 * chose the same languages read identically wherever they are listed;
 * duplicates and unknown codes fall out.
 */
export function teachingLanguages(stored: unknown): LanguageCode[] {
  const claimed = Array.isArray(stored) ? stored : [];
  const held = new Set(claimed.filter(isLanguage));
  return LANGUAGES.map((l) => l.code).filter((code) => held.has(code));
}

/**
 * Whether this معلم teaches in the language the pupil is reading the app in.
 *
 * A معلم who has declared NOTHING matches everybody. That is deliberate: the
 * field arrives with المرحلة 7 and every teacher already approved has an empty
 * one, so treating silence as "no languages" would empty the list on the day
 * this ships. Silence means "not said", and the filter's job is to narrow when
 * somebody has said something.
 */
export function teachesIn(stored: unknown, language: LanguageCode): boolean {
  const declared = teachingLanguages(stored);
  return declared.length === 0 || declared.includes(language);
}
